import type { PulseRun } from '../../../api/work';
import { NarrativeMarkdown } from './markdown';
import './insights.css';

/**
 * The composed narrative, and how old it is.
 *
 * The largest of the four Insights elements the port dropped. `work.pulse`
 * composes this with a model — live narratives on this instance run
 * 1,491 to 5,809 characters — and 425 runs of it were sitting in
 * `pulse_findings_runs` unreachable until `/api/work/pulse/latest` existed.
 *
 * **Staleness is stated, not implied.** A narrative describes a window at a
 * moment; read a day later it is a claim about the past wearing the present
 * tense. The predecessor showed a note past a threshold and this keeps it,
 * because the alternative is a reader trusting yesterday's summary of
 * today's board.
 *
 * **A run with no narrative is normal.** `work.pulse` writes the run when
 * the findings are computed and composes the narrative afterwards, so
 * between the two there is a run with findings and no text. That is a
 * pending state, not an error.
 */

/** Past this, the narrative is old enough that saying so matters. */
const STALE_AFTER_MS = 12 * 60 * 60 * 1000;

function age(ms: number): string {
  const min = Math.floor(ms / 60_000);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  return `${Math.floor(hr / 24)}d`;
}

interface Props {
  run: PulseRun | null;
  highlightedSlug?: string | null;
  onRefresh?: () => void;
  refreshing?: boolean;
}

export function NarrativeBlock({
  run,
  highlightedSlug,
  onRefresh,
  refreshing,
}: Props) {
  const composed = run?.narrative_composed_at_ms ?? null;
  const since = composed === null ? null : Date.now() - composed;
  const stale = since !== null && since > STALE_AFTER_MS;

  return (
    <section className="nb">
      <header className="nb__head">
        <span className="nb__label">Insights narrative</span>
        <span className="nb__meta">
          {composed !== null && (
            <span className={stale ? 'nb__stale' : 'in-dim'}>
              composed {age(since ?? 0)} ago
              {stale && ' — older than the window it describes'}
            </span>
          )}
          {run && (
            <span className="in-dim">
              {run.finding_count} finding
              {run.finding_count === 1 ? '' : 's'} · {run.window_days}d
              {run.trigger ? ` · ${run.trigger}` : ''}
            </span>
          )}
          {onRefresh && (
            <button
              type="button"
              className="nb__refresh"
              onClick={onRefresh}
              disabled={refreshing}
            >
              {refreshing ? 'composing…' : 'Recompose'}
            </button>
          )}
        </span>
      </header>

      {run === null ? (
        <p className="in-dim">
          Nothing composed for this window yet. <code>work.pulse</code> runs
          on a timer and writes a run before it composes the narrative, so a
          fresh window is empty rather than broken.
        </p>
      ) : run.narrative_text ? (
        <div className="nb__body">
          <NarrativeMarkdown
            text={run.narrative_text}
            highlightedSlug={highlightedSlug}
          />
        </div>
      ) : (
        <p className="in-dim">
          This run has {run.finding_count} finding
          {run.finding_count === 1 ? '' : 's'} and no narrative yet — the run
          is written when the findings are computed and the text is composed
          after. Pending, not failed.
        </p>
      )}
    </section>
  );
}
